import { createFileRoute, notFound } from "@tanstack/react-router"
import { SITE_URL } from "@/lib/constants/site-url"
import { jsonLd } from "@/lib/utils/json-ld"
import { COFFEE_LOTS, COFFEE_ROASTER } from "@/lib/data/coffee"
import CoffeePage from "@/pages/coffee"

// Dedicated share card: the Brazil Minas Gerais Luzia bag — the coffee in the
// espresso hopper — shot against the roastery's studio backdrop.
const OG_IMAGE = `${SITE_URL}/images/plusbake/optimized/og-coffee-1200x630.jpg`

export const Route = createFileRoute("/$countryCode/coffee")({
  loader: async ({ params, context }) => {
    const { countryCode } = params
    const { queryClient } = context
    const { getRegion } = await import("@/lib/data/regions")

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    return { countryCode, region }
  },
  head: () => {
    const title = "Coffee Shop in Rancho Cucamonga | +Bake Cafe"
    const description =
      "Espresso, pandan and Spanish lattes at +Bake Cafe in Rancho Cucamonga, pulled on Local Coffee Roasters beans roasted weekly. 12 oz bags to take home."
    const canonical = `${SITE_URL}/coffee`
    const businessId = `${SITE_URL}/#bakery`

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Our Coffee", item: canonical },
      ],
    }

    // The three current whole-bean retail lots as Products so answer engines
    // can attribute roaster, tasting notes, and price to the café directly.
    const beansSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Whole bean coffee at +Bake Cafe",
      url: canonical,
      itemListElement: COFFEE_LOTS.map((lot, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: `${lot.displayName} (12 oz whole bean)`,
          description: `${lot.process} process, ${lot.origin}. ${lot.tastingNotes}. Roasted weekly in ${COFFEE_ROASTER.city}, ${COFFEE_ROASTER.region} by ${COFFEE_ROASTER.name}.`,
          image: `${SITE_URL}${lot.image}`,
          brand: { "@type": "Brand", name: COFFEE_ROASTER.name },
          offers: {
            "@type": "Offer",
            price: lot.price.toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            seller: { "@id": businessId },
          },
        },
      })),
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:site_name", content: "+Bake Cafe" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
        { type: "application/ld+json", children: jsonLd(beansSchema) },
      ],
    }
  },
  component: CoffeePage,
})
